import { DEFAULT_DEBOUNCE_DELAY, STATUSPAGE } from '@repo/ui';
import debounce from 'debounce';
import { LIST_PAGINATED_TASKS_COLUMNS } from 'domain/models';
import {
  TaskUpdateUseCaseInterface,
  TaskCreateUseCaseInterface,
  TaskDeleteUseCaseInterface,
  TaskGetByIdUseCaseInterface,
  TaskListUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useDataTableRemotePagination } from 'presentation/app/hooks/use-datatable-remote-pagination';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadTask } from 'presentation/config/store/task/task-slice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

export const useTask = () => {
  // State
  const [taskByIdSelected, setTaskByIdSelected] =
    useState<TaskGetByIdUseCaseInterface.Result>();
  const dispatch = useDispatch();

  //Hooks
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { taskService } = useService();
  const location = useLocation();
  const {
    paginationMetadata,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearchRemote,
    handleSortRemote,
  } = useDataTableRemotePagination<
    TaskListUseCaseInterface.Result['data'][number],
    LIST_PAGINATED_TASKS_COLUMNS
  >({ initialColumnToOrderBy: LIST_PAGINATED_TASKS_COLUMNS.NAME });

  // Selectors
  const { taskList } = useAppSelector((state) => state.Task);
  const taskId = location?.state?.id;

  const [listStatus, setListStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  const [loadingTaskById, setLoadingTaskById] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [loadingCreateOrEditTask, setLoadingCreateOrEdit] =
    useState<STATUSPAGE>(STATUSPAGE.DONE);

  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // list course
  const requestTaskList = useCallback(
    async (params?: TaskListUseCaseInterface.Params) => {
      setListStatus(STATUSPAGE.LOADING);
      return taskService
        .taskList(params)
        .then((res) => {
          dispatch(loadTask(res));
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao listar tarefas');
        })
        .finally(() => {
          setListStatus(STATUSPAGE.DONE);
        });
    },
    [taskService, dispatch],
  );

  // Helper to build params from current pagination state
  const buildCurrentParams = useCallback(
    (): TaskListUseCaseInterface.Params => ({
      page: paginationMetadata.selectedPage,
      lenght: paginationMetadata.rowsPerPage,
      orderDirection: paginationMetadata.sortDirection,
      orderColumn: paginationMetadata.orderColumn,
      query: paginationMetadata.searchString,
    }),
    [paginationMetadata],
  );

  const refreshTaskList = useCallback(() => {
    requestTaskList(buildCurrentParams());
  }, [requestTaskList, buildCurrentParams]);

  const debouncedRequestTaskList = useMemo(
    () =>
      debounce((valuesToSubmit: TaskListUseCaseInterface.Params) => {
        requestTaskList(valuesToSubmit);
      }, DEFAULT_DEBOUNCE_DELAY),
    [requestTaskList],
  );

  useEffect(() => {
    debouncedRequestTaskList(buildCurrentParams());
  }, [paginationMetadata, debouncedRequestTaskList, buildCurrentParams]);

  // create couse
  const requestTaskCreate = useCallback(
    async (params: TaskCreateUseCaseInterface.Params) => {
      setLoadingCreateOrEdit(STATUSPAGE.LOADING);
      return taskService
        .taskCreate(params)
        .then((res) => {
          notifySuccess(res.message || 'Tarefa criado com sucesso');
          refreshTaskList();
          setIsModalOpen(false);
          setTaskByIdSelected(undefined);
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao criar uma tarefa');
        })
        .finally(() => {
          setLoadingCreateOrEdit(STATUSPAGE.DONE);
        });
    },
    [taskService, refreshTaskList],
  );

  // delete course
  const requestTaskDelete = useCallback(
    async (params: TaskDeleteUseCaseInterface.Params) => {
      return taskService
        .taskDelete(params)
        .then((res) => {
          notifySuccess(res.message || 'Tarefa excluída com sucesso.');
          refreshTaskList();
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao excluir uma tarefa.');
        });
    },
    [taskService, refreshTaskList],
  );

  // update course
  const requestTaskUpdate = useCallback(
    async (params: TaskUpdateUseCaseInterface.Params) => {
      setLoadingCreateOrEdit(STATUSPAGE.LOADING);
      return taskService
        .taskUpdate(params)
        .then((res) => {
          notifySuccess(res.message || 'Tarefa atualizado com sucesso.');
          refreshTaskList();
          setIsModalOpen(false);
          setTaskByIdSelected(undefined);
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao atualizar uma tarefa.');
        })
        .finally(() => {
          setLoadingCreateOrEdit(STATUSPAGE.DONE);
        });
    },
    [taskService, refreshTaskList],
  );

  // get course by id
  const requestTaskGetById = useCallback(
    async (params: TaskGetByIdUseCaseInterface.Params) => {
      setLoadingTaskById(STATUSPAGE.LOADING);
      return taskService
        .taskGetById(params)
        .then((res: TaskGetByIdUseCaseInterface.Result) => {
          setTaskByIdSelected(res);
        })
        .catch((err) => {
          notifyError(
            err.message || 'Um erro inesperado ocorreu, tente novamente.',
          );
        })
        .finally(() => {
          setLoadingTaskById(STATUSPAGE.DONE);
        });
    },
    [taskService],
  );

  // Formik initialization
  const validationFormTask = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: taskByIdSelected ? taskByIdSelected.name : '',
      feedback: taskByIdSelected ? taskByIdSelected.feedback : '',
      description: taskByIdSelected ? taskByIdSelected.description : '',
      responseRequired: taskByIdSelected
        ? taskByIdSelected.responseRequired
        : null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Campo é obrigatório'),
      responseRequired: Yup.string().required('Campo é obrigatório'),
    }),
    onSubmit: (values) => {
      const valueToSubmit = {
        name: values.name,
        feedback: values.feedback,
        description: values.description,
        responseRequired: values.responseRequired,
      };

      taskId
        ? requestTaskUpdate({ ...valueToSubmit, id: taskId })
        : requestTaskCreate(valueToSubmit);
    },
  });

  return {
    requestTaskList,
    requestTaskCreate,
    handleToggleModal,
    requestTaskDelete,
    requestTaskUpdate,
    requestTaskGetById,
    setTaskByIdSelected,
    taskId,
    taskList,
    listStatus,
    isModalOpen,
    loadingTaskById,
    loadingCreateOrEditTask,
    taskByIdSelected,
    validationFormTask,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearchRemote,
    handleSortRemote,
  };
};
